/**
 * WinbackPriceCard - Pricing card for winback offer in payment step
 *
 * Displays the special offer pricing when user accepts winback offer
 * and proceeds to payment. Shows original price crossed out, discounted price,
 * and savings amount.
 */

import type { WinbackPricingResponse } from '@/api/generated-catch-backend/model';
import '../paywall-reset.css';

interface WinbackPriceCardProps {
  pricing: WinbackPricingResponse | null;
  isLoading: boolean;
}

export function WinbackPriceCard({ pricing, isLoading }: WinbackPriceCardProps) {
  const originalPrice = pricing?.originalPriceCents
    ? pricing.originalPriceCents / 100
    : 119;
  const discountedPrice = pricing?.finalPriceCents
    ? pricing.finalPriceCents / 100
    : 49;
  const savingsPercent = originalPrice > 0
    ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
    : 60;

  return (
    <div className="paywall-reset paywall-border-card relative mt-3 w-full overflow-visible bg-white">
      {/* Special Offer badge */}
      <div className="absolute -top-[18px] left-6 rounded-lg bg-[#7cf1c7] px-2.5 py-1">
        <span className="whitespace-nowrap text-[10px] font-semibold text-[#183344]">
          Special Offer
        </span>
      </div>

      <div className="p-[26px]">
        {isLoading ? (
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-[#183344]">
              Catch Membership
            </span>
            <span className="text-lg font-bold text-[#183344]">Loading...</span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-1">
            {/* Product name and discounted price */}
            <div className="flex w-full items-center justify-between">
              <span className="text-lg font-bold text-[#183344]">
                Catch Membership
              </span>
              <span className="text-lg font-bold text-[#0c6a58]">
                ${discountedPrice}
              </span>
            </div>

            {/* Original price crossed out and savings */}
            <div className="flex w-full items-center justify-between">
              <span className="text-[16.91px] font-bold leading-[21px] text-[#0c6a58] line-through">
                ${originalPrice} annually
              </span>
              <span className="whitespace-nowrap text-xs font-bold text-[#0c6a58]">
                Save {savingsPercent}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
